import { useEffect, useState } from "react";
import resolveAudioUrl from "../../../utils/audio/resolveAudioUrl";
import {
  issueEngineMediaDescriptor,
  validateEngineMediaDescriptor,
} from "../services/engineProtectedMedia";

const REFRESH_LEAD_MS = 15000;
const cache = new Map();

export function clearEngineMediaDescriptorCache() {
  cache.clear();
}

export default function useEngineMediaSource({
  apiUrl,
  token,
  sessionId,
  trialId,
  stimulusInstanceId,
  sampleLink,
  consumerContext,
  protectedMediaPilot,
  descriptorIssuer = issueEngineMediaDescriptor,
  descriptorIssuerInput = {},
}) {
  const [protectedSource, setProtectedSource] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    let refreshTimer;
    if (!protectedMediaPilot) {
      setProtectedSource(null);
      setError("");
      setLoading(false);
      return () => { active = false; };
    }

    const key = `${consumerContext}:${sessionId}:${trialId}:${stimulusInstanceId}`;
    async function acquire(force = false) {
      setLoading(true);
      setError("");
      try {
        const cached = cache.get(key);
        let descriptor;
        if (!force && cached?.descriptor && validateEngineMediaDescriptor(
          cached.descriptor,
          stimulusInstanceId,
          consumerContext
        )) {
          descriptor = cached.descriptor;
        } else if (!force && cached?.promise) {
          descriptor = await cached.promise;
        } else {
          const promise = descriptorIssuer({
            apiUrl,
            token,
            sessionId,
            trialId,
            consumerContext,
            ...descriptorIssuerInput,
          });
          cache.set(key, { promise });
          descriptor = await promise;
          if (!validateEngineMediaDescriptor(
            descriptor,
            stimulusInstanceId,
            consumerContext
          )) throw new Error("PROTECTED_MEDIA_DESCRIPTOR_INVALID");
          cache.set(key, { descriptor });
        }
        if (!active) return;
        setProtectedSource(descriptor.playbackUrl);
        setLoading(false);
        const delay = Math.max(
          0,
          Date.parse(descriptor.expiresAt) - Date.now() - REFRESH_LEAD_MS
        );
        refreshTimer = window.setTimeout(
          () => acquire(true),
          Math.min(delay, 2147483647)
        );
      } catch (cause) {
        cache.delete(key);
        if (!active) return;
        setProtectedSource(null);
        setLoading(false);
        setError("Protected media could not be prepared.");
      }
    }
    if (sessionId && trialId && stimulusInstanceId) acquire();
    else setError("Protected media could not be prepared.");
    return () => {
      active = false;
      window.clearTimeout(refreshTimer);
    };
  }, [apiUrl, consumerContext, descriptorIssuer, descriptorIssuerInput, protectedMediaPilot, sessionId, stimulusInstanceId, token, trialId]);

  return {
    audioSrc: protectedMediaPilot
      ? protectedSource
      : resolveAudioUrl(sampleLink),
    error,
    loading,
    protected: protectedMediaPilot === true,
  };
}
